import { useStatus } from '@powersync/react';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Platform } from 'react-native';

import { powerSync } from '@/lib/powersync/db';

/**
 * The sync status banner's headline state. `'syncing'` is specifically
 * "reconnected after an outage, still flushing" — routine online uploads stay
 * `'synced'` (see `useSyncStatus`).
 */
export type SyncState = 'offline' | 'syncing' | 'synced';

const IS_WEB = Platform.OS === 'web';

/**
 * Web-only browser reachability via `navigator.onLine` + the `online`/`offline`
 * events. PowerSync's `connected` flag tracks its own sync stream, which doesn't
 * promptly drop when the browser goes offline (e.g. DevTools "Offline"), so on
 * web we layer this in as an additional offline signal. On native there is no
 * `navigator.onLine`, so this always returns `true` and connectivity stays driven
 * by PowerSync's `connected` flag.
 */
function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    IS_WEB && typeof navigator !== 'undefined' && 'onLine' in navigator
      ? navigator.onLine
      : true,
  );

  useEffect(() => {
    if (!IS_WEB || typeof window === 'undefined') return;
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return online;
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) if (!b.has(value)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Shared upload-queue poller
// ---------------------------------------------------------------------------
//
// ONE interval for the whole app. Both the global "N changes pending" count and
// the per-run "which instances are queued" set come from the local upload queue,
// so a single poll reads both once per tick and fans out to every subscriber —
// rather than each mounted screen (Home, a run) spinning its own interval.
//
// We poll (not a reactive watch) because watched queries don't reliably fire on
// the internal `ps_crud` table. `getUploadQueueStats`/`getCrudBatch` are
// non-consuming — they only SELECT; the queue drains via `complete()`, which we
// never call here. The snapshot keeps stable object/Set identities between ticks
// unless its values actually change, so steady-state polling doesn't re-render
// consumers.
const POLL_MS = 2500;

type QueueSnapshot = { count: number; instanceIds: Set<string> };

let queueSnapshot: QueueSnapshot = { count: 0, instanceIds: new Set() };
const queueListeners = new Set<() => void>();
let queueInterval: ReturnType<typeof setInterval> | null = null;

async function pollQueue(): Promise<void> {
  try {
    const stats = await powerSync.getUploadQueueStats(false);
    // Generous limit (the default is only 100) so the whole queue is read in one
    // pass; the local upload queue is tiny in practice. `.id` on an `instances` op
    // IS the instance id; child-table ops (items/notes) need no resolution.
    const batch = await powerSync.getCrudBatch(10000);
    const count = stats?.count ?? 0;
    const ids = new Set<string>();
    for (const op of batch?.crud ?? []) {
      if (op.table === 'instances') ids.add(op.id);
    }
    const idsChanged = !setsEqual(queueSnapshot.instanceIds, ids);
    if (count !== queueSnapshot.count || idsChanged) {
      queueSnapshot = {
        count,
        instanceIds: idsChanged ? ids : queueSnapshot.instanceIds,
      };
      for (const listener of queueListeners) listener();
    }
  } catch {
    // Ignore — best-effort UI hint.
  }
}

function subscribeQueue(listener: () => void): () => void {
  queueListeners.add(listener);
  if (!queueInterval) {
    void pollQueue();
    queueInterval = setInterval(pollQueue, POLL_MS);
  }
  return () => {
    queueListeners.delete(listener);
    if (queueListeners.size === 0 && queueInterval) {
      clearInterval(queueInterval);
      queueInterval = null;
    }
  };
}

/** Subscribe to the shared upload-queue snapshot (starts the poller on demand). */
function useQueueSnapshot(): QueueSnapshot {
  return useSyncExternalStore(subscribeQueue, () => queueSnapshot);
}

/**
 * Maps PowerSync's live sync status to the sync status banner's state, the
 * count of local changes still queued for upload (offline-first reassurance),
 * and the last completed sync time (the banner's freshness line).
 *
 * `'syncing'` means "reconnected, catching up after being offline" — it is
 * reported only when we were offline and still have work to flush. Routine
 * online edits constantly toggle PowerSync's `uploading` flag, so keying the
 * banner off `uploading` alone made it flash on every checkbox tap; gating on a
 * prior disconnect keeps the banner out of the steady-state path.
 *
 * `'offline'` is debounced: `connected` dips false for sub-second windows on
 * first launch and whenever another tab joins the shared sync worker, so we
 * only surface "Offline" once the disconnection has persisted past a grace
 * period — long enough to ride out those blips, short enough to still flag a
 * real outage. On web the offline signal also comes from `navigator.onLine` /
 * the browser `offline` event (PowerSync's `connected` flag lags a real
 * browser-offline), routed through the same grace period.
 */
const OFFLINE_GRACE_MS = 2000;

export function useSyncStatus(): {
  state: SyncState;
  pending: number;
  /** When the last sync completed, or null before the first-ever sync. */
  lastSyncedAt: Date | null;
} {
  const status = useStatus();
  const onLine = useOnlineStatus();
  const { count: pending } = useQueueSnapshot();
  const [wasOffline, setWasOffline] = useState(false);
  const [offlineConfirmed, setOfflineConfirmed] = useState(false);

  const connected = !!status?.connected;
  const uploading = !!status?.dataFlowStatus?.uploading;
  const offline = !connected || !onLine;

  // Clear the `wasOffline` latch once everything has flushed (connected, idle,
  // queue empty). React's "adjust state during render" pattern (see useSyncedDraft),
  // guarded by `wasOffline` so it can't loop — the reactive `pending` from the
  // shared poller makes "fully synced" observable without a setState-in-effect.
  if (wasOffline && onLine && connected && !uploading && pending === 0) {
    setWasOffline(false);
  }

  // Debounce the offline signal: reconnecting clears it (via cleanup), but
  // declaring "offline" waits for the disconnection to outlast OFFLINE_GRACE_MS.
  // Confirming offline also latches `wasOffline`, so the "reconnected, merging"
  // reassurance can show while catching up afterward (until the poll above
  // clears the latch).
  useEffect(() => {
    if (!offline) return;
    const id = setTimeout(() => {
      setOfflineConfirmed(true);
      setWasOffline(true);
    }, OFFLINE_GRACE_MS);
    return () => {
      clearTimeout(id);
      setOfflineConfirmed(false);
    };
  }, [offline]);

  const reconnecting = connected && wasOffline && (uploading || pending > 0);
  const state: SyncState = offlineConfirmed
    ? 'offline'
    : reconnecting
      ? 'syncing'
      : 'synced';
  return { state, pending, lastSyncedAt: status?.lastSyncedAt ?? null };
}

/**
 * Whether PowerSync has completed at least one sync. `hasSynced` is persisted in
 * the local DB, so returning users get `true` immediately (even offline); it is
 * only `false`/`undefined` during a genuine first-ever sync. Use this to keep
 * loading skeletons up until the initial sync lands, rather than flashing an
 * empty state off a local query that resolved before any data arrived.
 */
export function useHasSynced(): boolean {
  const status = useStatus();
  return !!status?.hasSynced;
}

/**
 * The set of instance ids whose local writes are still queued for upload —
 * driving the per-run "not synced yet" indicator (the global `pending` count
 * above can't tell which run is queued). Reads the shared upload-queue snapshot,
 * so it shares the single app-wide poller with `useSyncStatus`. Run completion is
 * an `UPDATE instances …`, so an unsynced completed run always has such an op
 * queued. The returned Set keeps a stable identity between ticks unless its
 * membership actually changes, so steady-state polling doesn't re-render consumers.
 */
export function usePendingInstanceIds(): Set<string> {
  return useQueueSnapshot().instanceIds;
}
